/**
 * Copyright © 2026 Delicious AI, LLC
 */

import type { SearchProduct } from './labelingHelpers'

const RECENT_REPLACEMENT_PRODUCTS_STORAGE_KEY =
  'whte-rbt:classification:recentReplacementProducts:v1'
const MAX_RECENT_REPLACEMENT_PRODUCTS = 12
export const RECENT_PRODUCTS_UPDATED_EVENT = 'whte-rbt:recent-products-updated'

export type ReplacementProductOption = SearchProduct & {
  resultGroup: 'Recent' | 'All products'
}

const sanitizeRecentReplacementProduct = (
  product: SearchProduct,
): SearchProduct => {
  const { resultGroup: _resultGroup, ...sanitized } =
    product as SearchProduct & { resultGroup?: string }
  return sanitized
}

const isSearchProductShape = (value: unknown): value is SearchProduct => {
  if (!value || typeof value !== 'object') return false
  const candidate = value as { id?: unknown; upcA?: unknown }
  return typeof candidate.id === 'string' && typeof candidate.upcA === 'string'
}

export const readRecentReplacementProducts = (): SearchProduct[] => {
  if (typeof window === 'undefined') return []

  try {
    const raw = window.localStorage.getItem(
      RECENT_REPLACEMENT_PRODUCTS_STORAGE_KEY,
    )
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(isSearchProductShape)
      .slice(0, MAX_RECENT_REPLACEMENT_PRODUCTS)
  } catch {
    return []
  }
}

const writeRecentReplacementProducts = (products: SearchProduct[]) => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(
      RECENT_REPLACEMENT_PRODUCTS_STORAGE_KEY,
      JSON.stringify(
        products
          .map(sanitizeRecentReplacementProduct)
          .slice(0, MAX_RECENT_REPLACEMENT_PRODUCTS),
      ),
    )
    window.dispatchEvent(new Event(RECENT_PRODUCTS_UPDATED_EVENT))
  } catch {
    // Best-effort cache only.
  }
}

export const rememberRecentReplacementProduct = (
  products: SearchProduct[],
  product: SearchProduct,
) => {
  const sanitized = sanitizeRecentReplacementProduct(product)
  const next = [
    sanitized,
    ...products.filter(recent => recent.upcA !== sanitized.upcA),
  ].slice(0, MAX_RECENT_REPLACEMENT_PRODUCTS)
  writeRecentReplacementProducts(next)
  return next
}

export const getRecentReplacementProductSearchText = (product: SearchProduct) =>
  [
    product.upcA,
    product.upcE,
    product.name,
    product.preferredName,
    product.commonName?.name,
    product.brand?.name,
    product.packaging?.packagingString,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

export const recentReplacementProductMatchesQuery = (
  product: SearchProduct,
  query: string,
) => {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (!tokens.length) return true

  const searchText = getRecentReplacementProductSearchText(product)
  return tokens.every(token => searchText.includes(token))
}

export const makeRecentReplacementFallbackProduct = (
  upc: string,
): SearchProduct =>
  ({
    id: upc,
    upcA: upc,
    upcE: null,
    name: upc,
    preferredName: upc,
    packagingName: null,
    isActive: true,
    marketingImageUrl: null,
    verifiedBy: null,
    commonName: null,
    brand: null,
    packaging: null,
    images: [],
    manufacturer: null,
  }) as unknown as SearchProduct
